import { useCallback } from 'react';
import { calculateDefaultExpirationDate } from 'utils/configUtilities';
import { CCI_FEATURE_ENABLED, cciBlockedByDualCpuReason } from 'utils/shouldUseCciPath';
import { useConfigInputs, useConfigActions, useVendorCtx } from 'shared/state/configContexts';
import { useQuoteActionsCtx } from 'shared/state/quoteActionsContext';

export type QuoteFieldName =
  | 'accountExecutive' | 'opportunityID' | 'quoteDescription' | 'preparedFor'
  | 'salesforceQuoteNumber' | 'notes' | 'partnerName' | 'warrantyStartDate' | 'quoteExpirationDate';

/** Quote Details section state: every field from the classic card (App.js
 *  ~1420-1582). The legacy identifiers (customerID, customerNumber,
 *  offerNumber, the quote serial) are not offered, as in classic — they stay
 *  on the quote and the serial is still assigned at generate time.
 *  Inline validation runs on blur through the Phase 0b `setFieldError`
 *  published by useQuoteActionsCtx — this hook never calls
 *  validateQuoteField directly. */
export function useQuoteDetailsSection() {
  const { metadata, viewState, config } = useConfigInputs();
  const { updateConfig } = useConfigActions();
  const { vendor } = useVendorCtx();
  const { quoteErrors, setFieldError, accountExecutives, handleGenerateQuotePDF, busy } = useQuoteActionsCtx();

  const set = useCallback((field: QuoteFieldName, value: unknown) => updateConfig({ [field]: value } as never), [updateConfig]);

  const onAccountExecutiveChange = useCallback((name: string) => {
    const exec = accountExecutives.find((e) => e.name === name);
    if (exec) updateConfig({ accountExecutive: exec.name, accountExecutiveEmail: exec.email } as never);
    setFieldError('accountExecutive', name);
  }, [accountExecutives, updateConfig, setFieldError]);

  return {
    metadata, quoteErrors, set, setFieldError,
    accountExecutives, onAccountExecutiveChange,
    accountExecutiveEmail: metadata.accountExecutiveEmail,
    defaultExpiration: calculateDefaultExpirationDate(),
    showCci: CCI_FEATURE_ENABLED && vendor.id === 'AIC',
    cciEnabled: viewState.cciEnabled === true,
    // CONF-210: null unless the flash set ships Dual CPU nodes, which no CCI
    // bundle covers; the toggle is disabled with this as its tooltip.
    cciDisabledReason: cciBlockedByDualCpuReason(config),
    onCciToggle: useCallback(() => updateConfig({ cciEnabled: !viewState.cciEnabled } as never), [viewState.cciEnabled, updateConfig]),
    onGenerate: handleGenerateQuotePDF,
    busy,
    summary: metadata.preparedFor ? String(metadata.preparedFor) : 'No customer yet',
  };
}
